import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ModalMethods, toasts } from '../../../ui-component';
import { api } from '../../../utils/apiRequest';
import emptyBox from '../../../assets/icons/image 356.png';
import piece from '../../../assets/images/Stack.png';
import wear from '../../../assets/images/Tag.png';
import customize from '../../../assets/images/custom.png';

import BarcodeScannerModal from './EditProduct/BarcodeScannerModal';
import ModifyVariantModal from './EditProduct/ModifyVariantModal';

import {
  convertNumberOrStringToPriceFormat,
  getValueFromLocalStorage,
  setValueInLocalStorage,
} from '../../../utils/common';

import type { Variant } from './EditProduct/ModifyVariantModal';

const ITEMS_ACTIVE_MAIN_TAB_KEY = 'itemsAndCategories_activeMainTab';
const ITEMS_ACTIVE_ORDER_TYPE_ID_KEY = 'itemsAndCategories_activeOrderTypeId';

type OutfitPiece = {
  id: number;
  name: string;
};

type TaxValue = {
  id?: number;
  operator?: string;
  price?: string;
  gst?: number;
  isValid?: boolean;
};

type TaxData = {
  label?: string;
  name?: string;
  value?: number | TaxValue | string;
  is_default?: boolean | null;
};

type PricingRule = {
  id: number;
  operator: string;
  price: string;
  gst: number;
};

type PricingMetaData = {
  id?: number;
  custom_outfit_id?: number;
  pricing_rules?: PricingRule[];
  advance_tax_name?: string;
  flat_gst?: number;
  flat_tax_name?: string;
  no_tax?: boolean;
  is_flat_gst_default?: boolean;
  tax_type?: 'NOTAX' | 'FLAT' | 'ADVANCE' | null;
};

type CustomOutfit = {
  id: number;
  boutique_id: number;
  outfit_name?: string;
  name?: string;
  service_name?: string;
  reference_id?: string | null;
  icon_url?: string;
  image_url?: string;
  icon?: string;
  outfit_pieces?: OutfitPiece[];
  pieces?: number | { name: string; image_ref_id?: string; image_url?: string }[];
  pieces_count?: number;
  quantity?: number;
  created_at?: string;
  is_valid?: boolean;
  category?: string | null;
  base_price?: number | null;
  price?: number | null;
  stitch_options_price?: number | null;
  options_price?: number | null;
  tax_data?: TaxData | null;
  pricing_meta_data?: PricingMetaData | null;
};

type ReadymadeProductTag = {
  id: number;
  name: string;
  boutique_id: number;
};

type ReadymadeProductUnit = {
  id: number;
  name: string;
  boutique_id: number;
};

type ReadymadeProductTaxData = {
  name?: string;
  value?: number;
  is_default?: boolean | null;
};

type ReadymadeProduct = {
  id: number;
  boutique_id: number;
  name: string;
  display_name?: string;
  gender?: string;
  category_id?: number;
  category?: string;
  tags?: ReadymadeProductTag[];
  gst_rate?: number;
  tax_data?: ReadymadeProductTaxData | null;
  tax_type?: string | null;
  base_selling_price?: number;
  min_value?: number;
  max_value?: number;
  unit?: ReadymadeProductUnit | null;
  cover_image_url?: string | null;
  cover_image_ref_id?: string | null;
  pieces_count?: number;
  order_type_id?: number;
  is_valid?: boolean;
};

type MainTabKey = 'products' | 'services';

type TabKey = 'custom_outfit' | 'readymade' | 'service';

// type TabItem = {
//   key: TabKey;
//   label: string;
//   apiKey: string;
// };

// const tabs: TabItem[] = [
//   { key: 'readymade', label: 'Products', apiKey: 'readymade' },
//   { key: 'service', label: 'Services', apiKey: 'service' },
// ];

type OrderType = {
  id: number;
  name: string;
  isActive: boolean;
  isCustom: boolean;
};

type ActiveOrderTypesResponse = {
  products?: {
    enabled: boolean;
    types: OrderType[];
  };
  services?: {
    enabled: boolean;
    types: OrderType[];
  };
};

const getInitialMainTab = (): MainTabKey => {
  const stored = getValueFromLocalStorage(ITEMS_ACTIVE_MAIN_TAB_KEY);

  return stored === 'products' || stored === 'services' ? stored : 'products';
};

const getInitialOrderTypeId = (): number | null => {
  const stored = getValueFromLocalStorage(ITEMS_ACTIVE_ORDER_TYPE_ID_KEY);

  return typeof stored === 'number' ? stored : null;
};

const ItemsAndCategories = () => {
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState<MainTabKey>(getInitialMainTab);
  const [activeTab, setActiveTab] = useState<TabKey>('readymade');
  const [items, setItems] = useState<(CustomOutfit | ReadymadeProduct)[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openMenuItemId, setOpenMenuItemId] = useState<number | null>(null);
  const [orderTypes, setOrderTypes] = useState<ActiveOrderTypesResponse | null>(null);
  const [activeOrderTypeId, setActiveOrderTypeId] = useState<number | null>(getInitialOrderTypeId);
  const menuRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef<boolean>(true);
  const barcodeScannerRef = useRef<ModalMethods>(null);
  const modifyVariantRef = useRef<ModalMethods>(null);
  const [scannedVariant, setScannedVariant] = useState<Variant | null>(null);
  const [scannedItemName, setScannedItemName] = useState<string>('');
  const [isScanSearching, setIsScanSearching] = useState(false);

  useEffect(() => {
    setValueInLocalStorage(ITEMS_ACTIVE_MAIN_TAB_KEY, activeMainTab);
    setValueInLocalStorage(ITEMS_ACTIVE_ORDER_TYPE_ID_KEY, activeOrderTypeId);
  }, [activeMainTab, activeOrderTypeId]);

  const closeMenu = useCallback(() => setOpenMenuItemId(null), []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (openMenuItemId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuItemId, closeMenu]);

  const handleDuplicateOutfit = useCallback(
    async (itemId: number) => {
      try {
        const response = await api.postRequest(`custom_outfit/duplicate/${itemId}`, {});

        if (response.status) {
          closeMenu();
          await fetchItems();
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'duplicate-outfit-error');
        }
      }
    },
    [activeTab, closeMenu]
  );

  const handleDeleteOutfit = useCallback(
    async (itemId: number) => {
      if (!window.confirm('Are you sure you want to delete this outfit?')) {
        return;
      }

      try {
        const response = await api.putRequest(`custom_outfit/delete/${itemId}`, {});

        if (response.status) {
          closeMenu();
          await fetchItems();
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'delete-outfit-error');
        }
      }
    },
    [activeTab, closeMenu]
  );

  const handleDuplicateProduct = useCallback(
    async (itemId: number) => {
      try {
        const response = await api.postRequest(`readymade/${itemId}/copy`, {});

        if (response.status) {
          closeMenu();
          await fetchItems();
        } else {
          toasts(
            'error',
            response.message || 'Failed to duplicate product',
            'duplicate-product-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'duplicate-product-error');
        }
      }
    },
    [closeMenu]
  );

  const handleDeleteProduct = useCallback(
    async (itemId: number) => {
      if (!window.confirm('Are you sure you want to delete this product?')) {
        return;
      }

      try {
        const response = await api.deleteRequest(`readymade/item/initial/${itemId}`);

        if (response.status) {
          closeMenu();
          await fetchItems();
        } else {
          toasts('error', response.message || 'Failed to delete product', 'delete-product-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'delete-product-error');
        }
      }
    },
    [closeMenu]
  );

  const handleBarcodeScan = useCallback(async (barcodeValue: string) => {
    try {
      setIsScanSearching(true);

      const response = await api.getRequest(
        `readymade/variants/search?barcode=${encodeURIComponent(barcodeValue)}`
      );

      if (response.status && response.data) {
        const variants = Array.isArray(response.data) ? response.data : [response.data];

        if (variants.length === 0) {
          toasts('error', 'No variant found for this barcode', 'barcode-search-empty');

          return;
        }

        const variant = variants[0] as Variant;

        let itemName = '';

        try {
          const productResponse = await api.getRequest(
            `readymade/${variant.readyMadeOutfitId}/full-details`
          );

          if (productResponse.status && productResponse.data?.item) {
            itemName =
              productResponse.data.item.display_name || productResponse.data.item.name || '';
          }
        } catch {
          // product name fetch is optional
        }

        setScannedVariant(variant);
        setScannedItemName(itemName);
        modifyVariantRef.current?.show();
      } else {
        toasts(
          'error',
          response.message || 'No variant found for this barcode',
          'barcode-search-error'
        );
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'barcode-search-error');
      }
    } finally {
      setIsScanSearching(false);
    }
  }, []);

  const fetchOrderTypes = useCallback(async () => {
    try {
      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'order-types-boutique-id-error');

        return;
      }

      const response = await api.getRequest(`api/boutiques/${boutiqueId}/order-types/active`);

      if (response.status && response.data) {
        const data = response.data as ActiveOrderTypesResponse;

        setOrderTypes(data);

        const productTypes = data.products?.types ?? [];
        const serviceTypes = data.services?.types ?? [];

        const getDefaultTypeId = (tab: MainTabKey): number | null => {
          const types = tab === 'products' ? productTypes : serviceTypes;

          if (!types.length) {
            return null;
          }

          const firstActive = types.find((type) => type.isActive);

          return (firstActive || types[0]).id;
        };

        setActiveOrderTypeId((prev) => {
          if (prev !== null) {
            const allTypes = [...productTypes, ...serviceTypes];

            if (allTypes.some((type) => type.id === prev)) {
              return prev;
            }
          }

          return getDefaultTypeId(activeMainTab);
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'order-types-load-error');
      }
    }
  }, [activeMainTab]);

  const fetchItems = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'items-boutique-id-error');

        return;
      }

      if (!activeOrderTypeId) {
        setItems([]);
        setErrorMessage(
          activeMainTab === 'products'
            ? 'Please select a product type to view items.'
            : 'Please select a service type to view items.'
        );

        return;
      }

      if (activeMainTab === 'products') {
        const response = await api.getRequest(`readymade/order-type/${activeOrderTypeId}/outfits`);

        if (response.status) {
          let itemsData: ReadymadeProduct[] = [];
          const data = response.data;

          if (data) {
            const rawList = Array.isArray(data)
              ? data
              : data.ready_made_outfits ??
                data.data?.ready_made_outfits ??
                data.readymade_response_list ??
                (Array.isArray(data.data) ? data.data : null);

            if (Array.isArray(rawList)) {
              itemsData = rawList;
            } else if (data && typeof data === 'object' && !Array.isArray(data)) {
              const possibleKeys = [
                'ready_made_outfits',
                'readymade_response_list',
                'data',
                'results',
                'items',
                'outfits',
              ];

              for (const key of possibleKeys) {
                const val = (data as Record<string, unknown>)[key];

                if (
                  Array.isArray(val) &&
                  val.length > 0 &&
                  typeof val[0] === 'object' &&
                  val[0] !== null &&
                  'id' in val[0]
                ) {
                  itemsData = val as ReadymadeProduct[];

                  break;
                }
              }
            }
          }

          setItems(itemsData);
          setErrorMessage('');
        } else {
          setErrorMessage(response.message || 'Failed to load items');
        }
      } else {
        const response = await api.getRequest(
          `custom_outfit/all/${boutiqueId}/order-type/${activeOrderTypeId}`
        );

        if (response.status) {
          let itemsData: CustomOutfit[] = [];

          if (response.data) {
            if (Array.isArray(response.data)) {
              itemsData = response.data;
            } else if (response.data.custom_outfit_response_list) {
              itemsData = Array.isArray(response.data.custom_outfit_response_list)
                ? response.data.custom_outfit_response_list
                : [];
            } else if (response.data.data && Array.isArray(response.data.data)) {
              itemsData = response.data.data;
            }
          }

          setItems(itemsData);
        } else {
          setErrorMessage(response.message || 'Failed to load items');
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        setErrorMessage(err.message);
        toasts('error', err.message, 'items-load-error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeMainTab, activeOrderTypeId]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }

    setIsLoading(true);
  }, [activeMainTab, activeOrderTypeId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    fetchOrderTypes();
  }, [fetchOrderTypes]);

  useEffect(() => {
    if (!orderTypes) {
      return;
    }

    const productTypes = orderTypes.products?.types ?? [];
    const serviceTypes = orderTypes.services?.types ?? [];
    const typesForCurrentMain = activeMainTab === 'products' ? productTypes : serviceTypes;

    if (!typesForCurrentMain.length) {
      setActiveOrderTypeId(null);

      return;
    }

    const hasCurrentInScope = typesForCurrentMain.some((type) => type.id === activeOrderTypeId);

    if (!hasCurrentInScope) {
      const firstActive = typesForCurrentMain.find((type) => type.isActive);

      setActiveOrderTypeId((firstActive || typesForCurrentMain[0]).id);
    }
  }, [activeMainTab, orderTypes, activeOrderTypeId]);

  useEffect(() => {
    if (!orderTypes) {
      return;
    }

    let nextTab: TabKey;

    if (activeMainTab === 'products') {
      nextTab = 'readymade';
    } else {
      const serviceTypes = orderTypes.services?.types ?? [];
      const selected = serviceTypes.find((type) => type.id === activeOrderTypeId) ?? null;

      if (selected && selected.isCustom) {
        nextTab = 'custom_outfit';
      } else {
        nextTab = 'service';
      }
    }

    setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [activeMainTab, activeOrderTypeId, orderTypes]);

  const filteredItems = useMemo(() => {
    if (!Array.isArray(items)) {
      return [];
    }

    if (!searchTerm.trim()) {
      return items;
    }

    const lower = searchTerm.toLowerCase();

    return items.filter((item) => {
      const name =
        'outfit_name' in item
          ? item.outfit_name || item.name || item.service_name || ''
          : item.name || (item as ReadymadeProduct).display_name || '';

      return name.toLowerCase().includes(lower);
    });
  }, [items, searchTerm]);

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-4 px-6 py-4 bg-white">
        <div>
          <p className="font-inter font-semibold text-sm leading-2 tracking-[0.07px] text-gray-600 mt-1 max-w-2xl">
            Configure all your items and services for different order types — add categories, set
            prices, and manage everything in one place.
          </p>
        </div>

        <div className="flex gap-3">
          {/* <Button label="+ Add Category" size="medium" bgColor="primary" /> */}
          <Button
            label="+ Add Item"
            size="medium"
            bgColor="primary"
            onClick={() => {
              if (activeMainTab === 'products') {
                if (!activeOrderTypeId) {
                  toasts('error', 'Select a product type first', 'add-item-order-type-missing');

                  return;
                }

                navigate('/settings/items-and-categories/add-item', {
                  state: { mode: 'readymade', orderTypeId: activeOrderTypeId },
                });
              } else {
                navigate('/settings/items-and-categories/add-item', {
                  state: { orderTypeId: activeOrderTypeId ?? undefined },
                });
              }
            }}
          />
        </div>
      </div>
    );
  };

  const renderTabs = () => {
    const isProductsEnabled = orderTypes?.products?.enabled ?? true;
    const isServicesEnabled = orderTypes?.services?.enabled ?? true;

    return (
      <div className=" w-full flex items-center gap-4 mb-4 px-6">
        <button
          type="button"
          onClick={() => setActiveMainTab('products')}
          disabled={!isProductsEnabled}
          className={`w-[22%] px-6 py-[10px] rounded-lg text-[16px] font-semibold transition-colors ${
            activeMainTab === 'products'
              ? 'bg-black text-white'
              : 'bg-white text-gray-600 border border-gray-300 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed'
          }`}
        >
          Products
        </button>

        <button
          type="button"
          onClick={() => setActiveMainTab('services')}
          disabled={!isServicesEnabled}
          className={`w-[22%] px-6 py-[10px] rounded-lg text-[16px] font-semibold transition-colors ${
            activeMainTab === 'services'
              ? 'bg-black text-white'
              : 'bg-white text-gray-600 border border-gray-300 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed'
          }`}
        >
          Services
        </button>
      </div>
    );
  };

  const renderOrderTypeTabs = () => {
    const types =
      activeMainTab === 'products'
        ? orderTypes?.products?.types ?? []
        : orderTypes?.services?.types ?? [];

    if (!types.length) {
      return null;
    }

    return (
      <div className="w-full flex flex-wrap items-center gap-3 mb-4 px-6">
        {types.map((type) => {
          const isActive = activeOrderTypeId === type.id;

          return (
            <button
              key={type.id}
              type="button"
              onClick={() => setActiveOrderTypeId(type.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${
                isActive
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
              }`}
            >
              <span>{type.name}</span>
              {activeMainTab === 'services' && type.isCustom && (
                <span className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-900 text-white">
                  Custom
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const renderSearch = () => {
    return (
      <div className="mb-4 px-6 flex items-center gap-3">
        <input
          type="text"
          placeholder="Search by item or category"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => barcodeScannerRef.current?.show()}
          disabled={isScanSearching}
          className="flex items-center gap-2 px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-60"
        >
          <span>{isScanSearching ? 'Searching...' : 'Scan'}</span>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="2" y="2" width="4" height="4" fill="currentColor" />
            <rect x="8" y="2" width="2" height="4" fill="currentColor" />
            <rect x="14" y="2" width="4" height="4" fill="currentColor" />
            <rect x="2" y="8" width="4" height="2" fill="currentColor" />
            <rect x="8" y="8" width="4" height="2" fill="currentColor" />
            <rect x="14" y="8" width="2" height="2" fill="currentColor" />
            <rect x="2" y="14" width="4" height="4" fill="currentColor" />
            <rect x="8" y="14" width="2" height="2" fill="currentColor" />
            <rect x="14" y="14" width="4" height="4" fill="currentColor" />
          </svg>
        </button>
      </div>
    );
  };

  const renderProductCard = (item: ReadymadeProduct) => {
    const itemName = item.name || item.display_name || 'Unnamed Product';
    const coverUrl = item.cover_image_url || null;
    const piecesCount = item.pieces_count ?? 0;
    const category = item.category || 'Uncategorized';
    const variantCount = item.tags?.length ?? 0;

    const getProductTaxLabels = (): string[] => {
      if (item.tax_type === 'NOTAX' || !item.tax_data?.name || item.tax_data.name === 'No Tax') {
        return ['No Tax'];
      }

      const labels: string[] = [];
      const name = item.tax_data.name;
      const gst = item.gst_rate;

      if (gst != null && gst > 0) {
        labels.push(`${name} (${gst}%)`);
      } else {
        labels.push(name);
      }

      return labels.length > 0 ? labels : ['No Tax'];
    };

    const taxLabels = getProductTaxLabels();
    const minVal = item.min_value ?? item.base_selling_price ?? 0;
    const maxVal = item.max_value ?? item.base_selling_price ?? 0;
    const priceRangeStr =
      minVal === maxVal
        ? `₹${convertNumberOrStringToPriceFormat(minVal)}`
        : `₹${convertNumberOrStringToPriceFormat(minVal)} - ₹${convertNumberOrStringToPriceFormat(
            maxVal
          )}`;

    return (
      <div
        key={item.id}
        className="bg-[#EAEEF833] border border-gray-200 rounded-2xl p-4 flex flex-col justify-between gap-2 shadow-sm relative max-h-[240px]"
      >
        <div
          ref={openMenuItemId === item.id ? menuRef : undefined}
          className="absolute top-4 right-4 z-10"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuItemId((prev) => (prev === item.id ? null : item.id));
            }}
            className="w-6 h-6 flex items-center justify-center text-[#4D7AFF] hover:text-[#4977ff]"
          >
            <svg
              width="6"
              height="20"
              viewBox="0 0 6 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="3" cy="3" r="2" fill="currentColor" />
              <circle cx="3" cy="12" r="2" fill="currentColor" />
              <circle cx="3" cy="21" r="2" fill="currentColor" />
            </svg>
          </button>

          {openMenuItemId === item.id && (
            <div className="absolute right-0 top-8 min-w-[180px] bg-white rounded-xl border border-gray-200 shadow-lg py-1 overflow-hidden">
              <div className="flex items-center justify-end pr-2 pt-1.5 pb-0.5">
                <button
                  type="button"
                  onClick={closeMenu}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 1l12 12M13 1L1 13" />
                  </svg>
                </button>
              </div>
              <button
                type="button"
                onClick={() => handleDuplicateProduct(item.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#4D7AFF] hover:bg-blue-50 text-sm font-medium"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <rect
                    x="5"
                    y="5"
                    width="8"
                    height="8"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="3"
                    y="3"
                    width="8"
                    height="8"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
                Duplicate outfit
              </button>
              <div className="border-t border-gray-100" />
              <button
                type="button"
                onClick={() => handleDeleteProduct(item.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-600 hover:bg-red-50 text-sm font-medium"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <path
                    d="M2 4h12v9a1 1 0 01-1 1H3a1 1 0 01-1-1V4zM5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v4M10 7v4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Delete Outfit
              </button>
              <div className="border-t border-gray-100" />
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  navigate('/settings/items-and-categories/add-item', {
                    state: {
                      editItem: item,
                      mode: 'readymade',
                      orderTypeId: item.order_type_id ?? activeOrderTypeId,
                    },
                  });
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-800 hover:bg-gray-50 text-sm font-medium"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <path
                    d="M11.5 2.5l2 2-8 8H2v-3.5l8-8 2 2z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Edit product
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <div className="w-24 h-24 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            {coverUrl ? (
              <img
                src={coverUrl}
                alt={itemName}
                className="w-[80%] h-[80%] object-cover rounded-xl"
              />
            ) : (
              <span className="text-3xl">👕</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-inter font-bold text-[15px] text-gray-900 mb-2 line-clamp-2 leading-none tracking-[0.07px]">
              {itemName}
            </p>

            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mb-3">
              {piecesCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <img src={piece} alt="Piece" className="w-3.5 h-3.5 object-contain" />
                  <span>{piecesCount} Piece</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <img src={wear} alt="Category" className="w-3.5 h-3.5 object-contain" />
                <span>{category}</span>
              </div>
              {variantCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <img src={wear} alt="Variants" className="w-3.5 h-3.5 object-contain" />
                  <span>{variantCount} Variants</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              {taxLabels.map((label) => (
                <span
                  key={label}
                  className="px-3 py-1.5 rounded-2xl bg-[#BAC9E8] text-xs font-medium text-gray-800"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-900">
            Item Total: <span className="font-bold">{priceRangeStr}</span>
          </p>

          <button
            type="button"
            className="px-4 py-2 border border-blue-500 text-blue-600 rounded-2xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
            onClick={() =>
              navigate(`/settings/items-and-categories/edit-product/${item.id}`, {
                state: { orderTypeId: item.order_type_id ?? activeOrderTypeId },
              })
            }
          >
            <img src={customize} alt="Customize" className="w-4 h-4 object-contain" />
            <span>Customize</span>
          </button>
        </div>
      </div>
    );
  };

  const renderItemCard = (item: CustomOutfit) => {
    const itemName = item.outfit_name || item.name || item.service_name || 'Unnamed Item';
    const iconUrl = item.icon_url || item.image_url || item.icon || null;
    const basePrice = item.base_price ?? item.price ?? 0;
    const optionPrice = item.stitch_options_price ?? item.options_price ?? 0;
    const totalPrice = basePrice + optionPrice;
    const pieces = Array.isArray(item.pieces)
      ? item.pieces.length
      : item.pieces_count ?? item.quantity ?? null;
    const category = item.category || 'Uncategorized';

    const getTaxDisplayText = (): string => {
      const meta = item.pricing_meta_data;
      const taxType = meta?.tax_type;

      if (taxType === 'ADVANCE') {
        const taxName = meta?.advance_tax_name || 'GST';
        const gstValues =
          meta?.pricing_rules
            ?.map((r) => r.gst)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .sort((a, b) => a - b) ?? [];
        const valueStr = gstValues.length > 0 ? `(${gstValues.map((v) => `${v}%`).join('-')})` : '';

        return `${taxName} ${valueStr}`.trim();
      }

      if (taxType === 'FLAT') {
        const taxName = meta?.flat_tax_name || 'GST';
        const gst = meta?.flat_gst ?? 0;

        return `${taxName} (${gst}%)`;
      }

      if (taxType === 'NOTAX') {
        return 'No Tax';
      }

      if (!meta?.tax_type) {
        return item.tax_data?.label ?? 'No Tax';
      }

      return 'No Tax';
    };

    const taxLabel = getTaxDisplayText();

    const optionCount = item.stitch_options_price || item.options_price ? 5 : 0;

    return (
      <div
        key={item.id}
        className="bg-[#EAEEF833] border border-gray-200 rounded-2xl p-4 flex flex-col justify-between  gap-2 shadow-sm relative max-h-[260px]"
      >
        <div
          ref={openMenuItemId === item.id ? menuRef : undefined}
          className="absolute top-4 right-4 z-10"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuItemId((prev) => (prev === item.id ? null : item.id));
            }}
            className="w-6 h-6 flex items-center justify-center text-[#4D7AFF] hover:text-[#4977ff]"
          >
            <svg
              width="6"
              height="20"
              viewBox="0 0 6 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="3" cy="3" r="2" fill="currentColor" />
              <circle cx="3" cy="12" r="2" fill="currentColor" />
              <circle cx="3" cy="21" r="2" fill="currentColor" />
            </svg>
          </button>

          {openMenuItemId === item.id && (
            <div className="absolute right-0 top-8 min-w-[180px] bg-white rounded-xl border border-gray-200 shadow-lg py-1 overflow-hidden">
              <div className="flex items-center justify-end pr-2 pt-1.5 pb-0.5">
                <button
                  type="button"
                  onClick={closeMenu}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 1l12 12M13 1L1 13" />
                  </svg>
                </button>
              </div>

              <button
                type="button"
                onClick={() => handleDuplicateOutfit(item.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#4D7AFF] hover:bg-blue-50 text-sm font-medium"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <rect
                    x="5"
                    y="5"
                    width="8"
                    height="8"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="3"
                    y="3"
                    width="8"
                    height="8"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
                Duplicate outfit
              </button>
              <div className="border-t border-gray-100" />
              <button
                type="button"
                onClick={() => handleDeleteOutfit(item.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-600 hover:bg-red-50 text-sm font-medium"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <path
                    d="M2 4h12v9a1 1 0 01-1 1H3a1 1 0 01-1-1V4zM5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v4M10 7v4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Delete Outfit
              </button>
              <div className="border-t border-gray-100" />

              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  navigate('/settings/items-and-categories/add-item', {
                    state: { editItem: item },
                  });
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-800 hover:bg-gray-50 text-sm font-medium"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <path
                    d="M11.5 2.5l2 2-8 8H2v-3.5l8-8 2 2z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Edit outfit
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <div className="w-24 h-24 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={itemName}
                className="w-[80%] h-[80%] object-cover rounded-xl"
              />
            ) : (
              <span className="text-3xl">👕</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-inter font-bold text-[15px] text-gray-900 mb-2 line-clamp-2 leading-none tracking-[0.07px]">
              {itemName}
            </p>

            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mb-3">
              {pieces && (
                <div className="flex items-center gap-1.5">
                  <img src={piece} alt="Piece" className="w-3.5 h-3.5 object-contain" />
                  <span>{pieces} Piece</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <img src={wear} alt="Category" className="w-3.5 h-3.5 object-contain" />
                <span>{category}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className="px-3 py-1.5 rounded-2xl bg-gray-100 text-xs font-medium text-gray-800">
                Base ₹{convertNumberOrStringToPriceFormat(basePrice)}
              </span>

              <span className="px-3 py-1.5 rounded-2xl bg-[#BAC9E8] text-xs font-medium text-gray-800">
                {optionCount} Opt. ₹{convertNumberOrStringToPriceFormat(optionPrice)}
              </span>

              <span className="px-3 py-1.5 rounded-2xl bg-[#BAC9E8] text-xs font-medium text-gray-800">
                {taxLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100 ">
          <p className="text-sm font-semibold text-gray-900">
            Item Total:{' '}
            <span className="font-bold">₹{convertNumberOrStringToPriceFormat(totalPrice)}</span>
          </p>

          <button
            type="button"
            className="px-4 py-2 border border-blue-500 text-blue-600 rounded-2xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
            onClick={() => navigate(`/settings/items-and-categories/edit-item/${item.id}`)}
          >
            <img src={customize} alt="Customize" className="w-4 h-4 object-contain" />
            <span>Customize</span>
          </button>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    const contentKey = `${activeMainTab}-${activeOrderTypeId}`;
    const minHeightClass = 'min-h-[60vh]';

    if (isLoading) {
      return (
        <div
          key={contentKey}
          className={`flex items-center justify-center py-10 ${minHeightClass} transition-opacity duration-200 ease-out opacity-100`}
        >
          <p className="text-sm text-gray-600">Loading items...</p>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div
          key={contentKey}
          className={`flex flex-col items-center justify-center py-20 ${minHeightClass} transition-opacity duration-200 ease-out opacity-100`}
        >
          <img
            src={emptyBox}
            alt="Empty store"
            className="w-28 h-28 object-contain mb-4 opacity-70"
          />
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      );
    }

    if (!filteredItems.length) {
      const isEmptyStore = !searchTerm.trim() && items.length === 0;

      if (isEmptyStore) {
        return (
          <div
            key={contentKey}
            className={`flex flex-col items-center justify-center py-20 ${minHeightClass} transition-opacity duration-200 ease-out opacity-100`}
          >
            <img
              src={emptyBox}
              alt="Empty store"
              className="w-28 h-28 object-contain mb-4 opacity-70"
            />
            <p className="text-2xl font-semibold text-gray-500 mb-1">Store is empty!</p>
            <p className="text-base text-gray-400">Add items or categories to get started</p>
          </div>
        );
      }

      return (
        <div
          key={contentKey}
          className={`flex flex-col items-center justify-center py-20 ${minHeightClass} transition-opacity duration-200 ease-out opacity-100`}
        >
          <img
            src={emptyBox}
            alt="Empty store"
            className="w-28 h-28 object-contain mb-4 opacity-70"
          />
          <p className="text-sm text-gray-600">No items found. Try adjusting your search.</p>
        </div>
      );
    }

    return (
      <div
        key={contentKey}
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-6 ${minHeightClass} transition-opacity duration-200 ease-out opacity-100`}
      >
        {filteredItems.map((item) =>
          activeMainTab === 'products'
            ? renderProductCard(item as ReadymadeProduct)
            : renderItemCard(item as CustomOutfit)
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full bg-[#F8F8FD]  py-4 overflow-y-auto">
      {renderHeader()}
      {renderTabs()}
      {renderOrderTypeTabs()}
      {renderSearch()}
      {renderTabContent()}

      <BarcodeScannerModal ref={barcodeScannerRef} onScanned={handleBarcodeScan} />

      <ModifyVariantModal
        ref={modifyVariantRef}
        variant={scannedVariant}
        itemName={scannedItemName}
        onSaved={fetchItems}
      />
    </div>
  );
};

export default ItemsAndCategories;
